import React from 'react';
import { Alert, Badge, Code, Group, List, Loader, Text, Tooltip } from '@mantine/core';
import { formatUsd, costByModelLabel } from '../utils/format';
import { TERMINAL } from '../hooks/useJobPoll';

// The one job-progress/result alert every panel renders (was triplicated across DesignPanel /
// DiagramsPanel / GenerationPanel). A TERMINAL job stays rendered — with its actual cost, steps,
// error, and any children (e.g. the revision ChangeSummary) — until the engineer dismisses it;
// background project refreshes no longer erase it (P0.3). `stalled` comes from useJobPoll.
export default function JobStatusAlert({ job, stalled, title, onDismiss, children }) {
  if (!job) return null;
  const terminal = TERMINAL.includes(job.status);
  const color = job.status === 'FAILED' ? 'red' : job.status === 'SUCCEEDED' ? 'green' : 'blue';
  return (
    <Alert
      mt="md"
      color={color}
      icon={terminal ? undefined : <Loader size={14} />}
      withCloseButton={terminal && !!onDismiss}
      onClose={onDismiss}
      closeButtonLabel="Dismiss result"
    >
      <Group gap="xs">
        <Text size="sm">
          {title || 'Job'} {job.jobId ? <Code>{job.jobId}</Code> : null} — {job.status}
        </Text>
        {job.estimatedCostUsd != null && job.status !== 'SUCCEEDED' && (
          <Badge variant="light" color="gray">
            est. {formatUsd(job.estimatedCostUsd)} / ~{Math.round((job.estimatedDurationMs || 0) / 1000)}s
          </Badge>
        )}
        {job.status === 'SUCCEEDED' && job.result?.cost && (
          <Tooltip label={costByModelLabel(job.result.cost.byModel) || 'Actual Bedrock spend for this pass'} withArrow>
            <Badge variant="light" color="teal">this pass: {formatUsd(job.result.cost.totalUsd)}</Badge>
          </Tooltip>
        )}
      </Group>
      {job.status === 'FAILED' && job.error?.message && (
        <Text size="sm" c="red" mt={4}>{job.error.message}</Text>
      )}
      {(job.completedSteps || []).length > 0 && (
        <List size="xs" mt="xs">{job.completedSteps.map((s, i) => <List.Item key={i}>{s}</List.Item>)}</List>
      )}
      {job.status === 'PENDING' && (
        <Text size="xs" c="dimmed" mt="xs">
          Queued. (If this doesn't advance, the state machine ARN isn't configured in this environment.)
        </Text>
      )}
      {stalled && (
        <Text size="xs" c="orange" mt="xs">
          This job hasn't started after 90s — it looks stuck. Check the state-machine configuration; the
          generation lock self-heals when the lease expires.
        </Text>
      )}
      {children}
    </Alert>
  );
}
